import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Upload, Trash2, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventLogoUpload = ({ event, onLogoUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);
  const { token } = useAuth();

  // Load logo from cloud storage if available
  useEffect(() => {
    const loadLogo = async () => {
      if (event?.logo_file_id) {
        try {
          const response = await axios.get(
            `${API_URL}/api/files/download/${event.logo_file_id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            }
          );
          const blobUrl = URL.createObjectURL(response.data);
          setLogoUrl(blobUrl);
        } catch (error) {
          console.error('Failed to load logo:', error);
          // Fallback to base64 logo if cloud fetch fails
          if (event?.logo) {
            setLogoUrl(event.logo);
          }
        }
      } else if (event?.logo) {
        // Legacy base64 logo
        setLogoUrl(event.logo);
      }
    };

    loadLogo();

    // Cleanup blob URL on unmount
    return () => {
      if (logoUrl && logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoUrl);
      }
    };
  }, [event?.logo_file_id, event?.logo, token]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB for cloud storage)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      // Upload to cloud storage
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/events/${event.id}/logo/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Logo uploaded successfully!');
      
      // Revoke old blob URL if exists
      if (logoUrl && logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoUrl);
      }

      // Fetch the newly uploaded logo
      const logoResponse = await axios.get(
        `${API_URL}/api/files/download/${response.data.file_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blobUrl = URL.createObjectURL(logoResponse.data);
      setLogoUrl(blobUrl);

      if (onLogoUpdate) {
        onLogoUpdate({
          ...event,
          logo_file_id: response.data.file_id,
          logo_path: response.data.path
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/events/${event.id}/logo`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Revoke blob URL if exists
      if (logoUrl && logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(logoUrl);
      }
      setLogoUrl(null);
      
      toast.success('Logo removed');
      if (onLogoUpdate) {
        onLogoUpdate({ ...event, logo: null, logo_file_id: null, logo_path: null });
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to remove logo');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="h-5 w-5" />
          Event Logo
        </CardTitle>
        <CardDescription>
          Upload a custom logo for your reunion event. This will be displayed on invitations and the event page.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Current Logo Preview */}
          {logoUrl ? (
            <div className="flex flex-col items-center gap-4">
              <div className="relative w-32 h-32 rounded-xl overflow-hidden border-2 border-gray-200 shadow-md">
                <img 
                  src={logoUrl} 
                  alt="Event Logo" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Change Logo
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteLogo}
                  disabled={deleting}
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-maroon hover:bg-cream-paper/50 transition-all"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <Upload className="h-8 w-8 text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-700">Upload Event Logo</p>
                  <p className="text-sm text-gray-500">PNG, JPG, GIF, WebP up to 5MB</p>
                </div>
                <Button variant="outline" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Select File'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="logo-file-input"
          />

          {/* Tips */}
          <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
            <p className="font-medium mb-1">Tips for best results:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-600">
              <li>Use a square image (1:1 ratio)</li>
              <li>Minimum 200x200 pixels recommended</li>
              <li>Use PNG for logos with transparent backgrounds</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EventLogoUpload;
